import axios from 'axios';
import {
  getConvAiAuthHeaders,
  getDispatcherHost,
  invalidateConvAiSessionToken,
  type ConvAiAuthHeaders,
} from './services/convAiSession.service';
import { getAgencyContext } from '@/shared/api/agencyContextStore';
import { convAiDebug } from './services/convAiDebug';
import type {
  MorningBriefingResponse,
  ShiftHandoverResponse,
  ShiftHandoverStatus,
} from './types/alpha.types';

/** Fallback used when no runtime config `web.ccagent.conv_base_url` is set (e.g. localhost). */
const DEFAULT_CONV_UI_BASE_URL = 'https://dev-riderapp.ecolane.com/conv-phase2';

/**
 * Base URL every Conv-AI call is built from, in priority order:
 *
 *  1. `VITE_CONV_UI_BASE_URL` — dev server only. vite.config.ts injects the
 *     same-origin proxy prefix (`/conv-ai`) here and forwards it to
 *     DEV_CONV_AI_TARGET, so local dev never makes the cross-origin call
 *     (CORS) and stays inside the index.html CSP (`connect-src 'self'`).
 *     Empty in a build, so this branch never fires in a deployed bundle.
 *  2. Runtime config `web.ccagent.conv_base_url` (passed in by the caller).
 *  3. DEFAULT_CONV_UI_BASE_URL.
 *
 * Mirrors getDispatcherHost() in convAiSession.service.ts — same dev-injection
 * pattern, same DEV guard.
 */
export const resolveConvUiBaseUrl = (convUiBaseUrl?: string): string => {
  const devProxyBase = (import.meta.env.VITE_CONV_UI_BASE_URL as string | undefined)?.trim();
  if (import.meta.env.DEV && devProxyBase) {
    return devProxyBase;
  }
  return convUiBaseUrl ?? DEFAULT_CONV_UI_BASE_URL;
};

/**
 * Interim Conv-AI auth (until TT-Auth): every Conv-AI call carries a
 * short-lived opaque key minted by the BFF as `Authorization: Bearer <key>`,
 * plus `X-Dispatcher-Host` — the agency hostname this session is bound to, which
 * Conv-AI forwards to the shared BFF so it resolves the right tenant.
 * The dispatcher session cookie is NEVER sent cross-origin to Conv-AI
 * (no withCredentials / credentials:'include'), and the raw dispatcher_bff_sid
 * is never exposed to JavaScript.
 *
 * On a 401 from Conv-AI the cached key is dropped, a fresh one is minted (which
 * only succeeds while the Dispatcher session is still valid) and the request
 * is retried exactly once.
 */
class ConvAiUnauthorizedError extends Error {
  constructor() {
    super('Conv-AI rejected the session key (401)');
    this.name = 'ConvAiUnauthorizedError';
  }
}

const isUnauthorized = (err: unknown): boolean =>
  err instanceof ConvAiUnauthorizedError ||
  (axios.isAxiosError(err) && err.response?.status === 401);

export const withConvAiAuth = async <T>(
  run: (authHeaders: ConvAiAuthHeaders) => Promise<T>,
): Promise<T> => {
  try {
    return await run(await getConvAiAuthHeaders());
  } catch (err) {
    if (!isUnauthorized(err)) throw err;
    invalidateConvAiSessionToken();
    return run(await getConvAiAuthHeaders());
  }
};

/**
 * The product this request comes from. Dispatcher UI and TransitOS both reach
 * the same Conv-AI endpoint, so the backend uses this for routing/telemetry —
 * never for authorization.
 */
const PRODUCT_CODE = 'ecolane';

/**
 * The scope this question is being asked in.
 *
 * Read from the already-loaded session state (agencyContextStore, written by
 * AuthProvider on /session bootstrap) — NOT a fresh network call per message,
 * and never a hardcoded agency.
 *
 * A STATEMENT, NOT A GRANT. Conv-AI resolves the authoritative agency from its
 * own BFF introspection of the bearer and answers 403 when the two disagree;
 * it never takes the agency from here. So the worst a wrong value can do is
 * get the request rejected — it cannot reach another agency's data.
 *
 * Returns undefined when the session has not bootstrapped and there is nothing
 * truthful to say. Omitting the block is safe and backward compatible: Conv-AI
 * still runs the request under the introspected agency, it simply has no claim
 * to check it against. Inventing a default agency here would be the one
 * genuinely unsafe option.
 */
interface ConvAiRequestContext {
  agency_id?: string;
  tenant_id?: string;
  product_code: string;
  product_context: { host: string };
}

const buildRequestContext = (): ConvAiRequestContext | undefined => {
  const { agencyId, tenantId } = getAgencyContext();
  if (!agencyId && !tenantId) return undefined;

  return {
    ...(agencyId ? { agency_id: agencyId } : {}),
    ...(tenantId ? { tenant_id: tenantId } : {}),
    product_code: PRODUCT_CODE,
    product_context: { host: getDispatcherHost() },
  };
};

// Module-level thread ID — persists across messages in the same session
let _currentThreadId: string | null = null;

export const resetThreadId = (): void => {
  _currentThreadId = null;
};

export interface ChatResponse {
  response: any;
}

export const sendMessage = async (question: string, convUiBaseUrl?: string): Promise<ChatResponse> => {
  const apiUrl = `${resolveConvUiBaseUrl(convUiBaseUrl)}/query`;
  const context = buildRequestContext();

  // Scope only — never the bearer, the session key, or the answer.
  convAiDebug('REQUEST', {
    agency: context?.agency_id ?? '(none)',
    tenant: context?.tenant_id ?? '(none)',
    product: context?.product_code ?? '(none)',
    dispatcherHost: getDispatcherHost(),
  });

  const response = await withConvAiAuth((authHeaders) =>
    axios.post(
      apiUrl,
      {
        question,
        thread_id: _currentThreadId,   // null on first turn → server generates one
        // Omitted entirely when the session has not bootstrapped — see
        // buildRequestContext. An absent block is backward compatible.
        ...(context ? { context } : {}),
      },
      { headers: authHeaders },
    ),
  );
  const data = response.data;

  // Store thread_id from response for next turn
  if (data?.thread_id) {
    _currentThreadId = data.thread_id;
  }

  if (data.response) {
    let responseData = data.response;

    // Keep parsing until the response is not a stringified JSON
    while (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch (error) {
        // Not a JSON string, break the loop
        break;
      }
    }

    // If after all parsing, the result is a string, wrap it in the object structure the UI expects
    if (typeof responseData === 'string') {
      responseData = { type: 'text', raw: responseData };
    } else if (typeof responseData === 'object' && responseData !== null) {
      // If the object has a 'response' property, it's likely the nested structure we've seen.
      // We'll prefer the 'response' property's content.
      if (responseData.response) {
        let nestedResponse = responseData.response;
        // Also check if this nested response is a stringified JSON
        while (typeof nestedResponse === 'string') {
          try {
            nestedResponse = JSON.parse(nestedResponse);
          } catch (error) {
            break;
          }
        }

        // If the deeply nested response is a string, wrap it.
        if (typeof nestedResponse === 'string') {
          responseData = { type: 'text', raw: nestedResponse };
        } else {
          responseData = nestedResponse;
        }
      }
    }

    data.response = responseData;
  }

  return data;
};

export const sendAudio = async (audioBlob: Blob, convUiBaseUrl?: string): Promise<ChatResponse> => {
  const apiUrl = `${resolveConvUiBaseUrl(convUiBaseUrl)}/query`;
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.wav');

  const response = await withConvAiAuth((authHeaders) =>
    axios.post(apiUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authHeaders,
      },
    }),
  );

  const data = response.data;

  if (data.response) {
    let responseData = data.response;

    // Keep parsing until the response is not a stringified JSON
    while (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch (error) {
        // Not a JSON string, break the loop
        break;
      }
    }
    
    // If after all parsing, the result is a string, wrap it in the object structure the UI expects
    if (typeof responseData === 'string') {
      responseData = { type: 'text', raw: responseData };
    } else if (typeof responseData === 'object' && responseData !== null) {
      // If the object has a 'response' property, it's likely the nested structure we've seen.
      // We'll prefer the 'response' property's content.
      if (responseData.response) {
        let nestedResponse = responseData.response;
        // Also check if this nested response is a stringified JSON
        while (typeof nestedResponse === 'string') {
          try {
            nestedResponse = JSON.parse(nestedResponse);
          } catch (error) {
            break;
          }
        }

        // If the deeply nested response is a string, wrap it.
        if (typeof nestedResponse === 'string') {
          responseData = { type: 'text', raw: nestedResponse };
        } else {
          responseData = nestedResponse;
        }
      }
    }
    
    data.response = responseData;
  }

  return data;
};

/**
 * Turns a microphone recording into text — and nothing more.
 *
 * Voice is only another way to type: the caller hands the returned words to the
 * same sendMessage() a typed question goes through, so a spoken question shares
 * the typed one's endpoint, thread_id, request context, auth and rendering.
 *
 *   POST {conv base}/voice/transcribe   multipart: file, mime_type, input_audio_format
 *   → { transcription: string }
 *
 * This used to be sendVoice(), which POSTed {conv base}/voice — a V1 conv-ui
 * route that ran its own conversation turn over SSE. The base URL moved to
 * conv-phase2, which never served /voice (404), and that is why voice stopped
 * working. Same base URL and the same withConvAiAuth wrapper as sendMessage.
 */
export const transcribeVoice = async (audioBlob: Blob, convUiBaseUrl?: string): Promise<string> => {
  const url = `${resolveConvUiBaseUrl(convUiBaseUrl)}/voice/transcribe`;
  const mimeType = audioBlob.type || 'audio/pcm';
  const isPcm = mimeType.includes('pcm');

  const formData = new FormData();
  formData.append('file', audioBlob, isPcm ? 'voice-input.pcm' : 'voice-input.webm');
  formData.append('mime_type', mimeType);
  if (isPcm) {
    formData.append('input_audio_format', 'pcm16');
  }

  const response = await withConvAiAuth(async (authHeaders) => {
    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      // No Content-Type: the browser adds the multipart boundary.
      headers: authHeaders,
    });
    if (res.status === 401) throw new ConvAiUnauthorizedError();
    return res;
  });

  if (!response.ok) {
    throw new Error(
      response.status === 400
        ? 'No audio was recorded.'
        : response.status === 413
          ? 'The recording is too long.'
          : `Voice input could not be transcribed (${response.status}).`,
    );
  }

  const data = (await response.json()) as { transcription?: unknown };
  return typeof data.transcription === 'string' ? data.transcription.trim() : '';
};

/**
 * Audio for an answer the chat has ALREADY received — speech synthesis only.
 *
 *   POST {conv base}/voice/speak   { text }   → audio/mpeg bytes
 *
 * Conv-AI voices `text` through AiDE text-to-speech, word for word; it does not
 * start a turn or compose anything, so a question is still exactly one
 * sendMessage(). Same base URL and withConvAiAuth wrapper as every other call.
 * `signal` lets read-aloud abandon the request when the dispatcher moves on.
 */
export const synthesizeSpeech = async (
  text: string,
  convUiBaseUrl?: string,
  signal?: AbortSignal,
): Promise<ArrayBuffer> => {
  const url = `${resolveConvUiBaseUrl(convUiBaseUrl)}/voice/speak`;

  const response = await withConvAiAuth(async (authHeaders) => {
    const res = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ text }),
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      ...(signal ? { signal } : {}),
    });
    if (res.status === 401) throw new ConvAiUnauthorizedError();
    return res;
  });

  if (!response.ok) {
    throw new Error(`The answer could not be read aloud (${response.status}).`);
  }
  const audio = await response.arrayBuffer();
  if (!audio.byteLength) {
    throw new Error('The answer could not be read aloud (no audio).');
  }
  return audio;
};


/**
 * The agency's Morning Briefing — the SAME cached record the chat answers
 * "Give me the morning briefing" from.
 *
 *   GET  {conv base}/briefing          → { briefing, sections, cache }
 *   POST {conv base}/briefing/refresh  → the same shape, regenerated for the agency
 *
 * Read-only for the browser. Conv-AI generates the briefing once per agency and
 * service day and shares it with every dispatcher of that agency; a cache hit
 * makes no source call and no AI call. Same base URL and the same withConvAiAuth
 * wrapper (BFF-minted key + X-Dispatcher-Host) as every other Conv-AI call, so the
 * agency is the one the BFF vouches for — never a value from this page.
 */
export const fetchMorningBriefing = async (
  convUiBaseUrl?: string,
  { refresh = false }: { refresh?: boolean } = {},
): Promise<MorningBriefingResponse> => {
  const base = resolveConvUiBaseUrl(convUiBaseUrl);
  const response = await withConvAiAuth((authHeaders) =>
    refresh
      ? axios.post<MorningBriefingResponse>(`${base}/briefing/refresh`, {}, { headers: authHeaders })
      : axios.get<MorningBriefingResponse>(`${base}/briefing`, { headers: authHeaders }),
  );
  const data = response.data;
  if (!data || typeof data !== 'object' || !('briefing' in data)) {
    throw new Error('The morning briefing response was malformed.');
  }
  return data;
};

/** The question the briefing card asks on the dispatcher's behalf — a cache hit, never a second generation. */
export const MORNING_BRIEFING_QUESTION = 'Give me the morning briefing.';

/**
 * The agency's End-of-Shift Handover — the SAME cached record the chat answers
 * "Generate the end-of-shift handover" from.
 *
 *   GET  {conv base}/handover          → { handover, sections, cache }
 *   POST {conv base}/handover/refresh  → the same shape, regenerated for the agency
 *
 * Read-only for the browser. Conv-AI generates ONE handover per agency per fixed
 * shift; the outgoing dispatcher's generation is what every incoming dispatcher
 * reads, with the same generated_at. Same base URL and the same withConvAiAuth
 * wrapper as every other Conv-AI call, so the agency and the shift are the
 * backend's — never a value from this page.
 */
export const fetchShiftHandover = async (
  convUiBaseUrl?: string,
  { refresh = false }: { refresh?: boolean } = {},
): Promise<ShiftHandoverResponse> => {
  const base = resolveConvUiBaseUrl(convUiBaseUrl);
  const response = await withConvAiAuth((authHeaders) =>
    refresh
      ? axios.post<ShiftHandoverResponse>(`${base}/handover/refresh`, {}, { headers: authHeaders })
      : axios.get<ShiftHandoverResponse>(`${base}/handover`, { headers: authHeaders }),
  );
  const data = response.data;
  if (!data || typeof data !== 'object' || !('handover' in data)) {
    throw new Error('The shift handover response was malformed.');
  }
  return data;
};

/**
 * Whether a handover already exists for this agency's current shift, and whether
 * that shift is near its end — a cache PEEK. It NEVER generates one, so the card
 * may poll it; only an explicit "generate"/"view" causes a generation.
 */
export const fetchShiftHandoverStatus = async (convUiBaseUrl?: string): Promise<ShiftHandoverStatus> => {
  const base = resolveConvUiBaseUrl(convUiBaseUrl);
  const response = await withConvAiAuth((authHeaders) =>
    axios.get<ShiftHandoverStatus>(`${base}/handover/status`, { headers: authHeaders }),
  );
  const data = response.data;
  if (!data || typeof data !== 'object' || !('shift' in data) || !('exists' in data)) {
    throw new Error('The shift handover status response was malformed.');
  }
  return data;
};

/** The question the handover card asks on the dispatcher's behalf — a cache hit once one exists. */
export const SHIFT_HANDOVER_QUESTION = 'Generate the end-of-shift handover.';
